import React, { useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { PG, pgBtn } from './theme';
import { byUpdatedDesc, formatWhen } from './helpers';

export type ChainPick = {
  id: string;
  name: string;
  description?: string | null;
  updated_at?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  chains: ChainPick[];
  attachedIds: string[];
  onAttach: (chain: ChainPick) => void;
  onDetach: (id: string) => void;
};

export const ChainPicker: React.FC<Props> = ({
  open,
  onClose,
  chains,
  attachedIds,
  onAttach,
  onDetach,
}) => {
  const [q, setQ] = useState('');
  const attached = useMemo(() => new Set(attachedIds), [attachedIds]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = needle
      ? chains.filter((c) => `${c.name} ${c.description || ''}`.toLowerCase().includes(needle))
      : chains;
    return [...rows].sort(byUpdatedDesc);
  }, [chains, q]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text, maxHeight: '86vh' } }}
    >
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        Attach a chain
        <Typography variant="body2" sx={{ color: PG.muted, fontWeight: 400, mt: 0.5, lineHeight: 1.45 }}>
          Preview can run an attached chain as you. Newest first. Type <strong>@</strong> in chat to pick one without this dialog.
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: PG.muted }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search chains by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <List dense disablePadding sx={{ mt: 1 }}>
          {filtered.length === 0 && (
            <Typography variant="body2" sx={{ color: PG.muted, py: 2 }}>
              No chains match.
            </Typography>
          )}
          {filtered.map((c, i) => {
            const isOn = attached.has(c.id);
            const latest =
              filtered.findIndex((x) => x.name.toLowerCase() === c.name.toLowerCase()) === i;
            const when = formatWhen(c.updated_at);
            return (
              <ListItemButton key={c.id} sx={{ alignItems: 'flex-start', py: 1 }}>
                <ListItemText
                  primary={c.name}
                  secondary={[latest ? 'Latest' : '', when, c.description || 'EasyAI chain']
                    .filter(Boolean)
                    .join(' · ')}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
                  secondaryTypographyProps={{ fontSize: 12, color: PG.muted }}
                />
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
                  {isOn ? (
                    <Chip
                      size="small"
                      label="Attached"
                      onDelete={() => onDetach(c.id)}
                      sx={{ bgcolor: 'rgba(56,189,248,0.2)', color: PG.text }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onAttach(c)}
                      sx={{ ...pgBtn, bgcolor: PG.accent, color: '#042f2e', fontWeight: 800, minHeight: 28 }}
                    >
                      Add
                    </Button>
                  )}
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose} sx={{ ...pgBtn, color: PG.text }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
